import { Box, CardTabs, GetColorStatus, capitalizeFirstLetter, Ticket } from 'vopay';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import useFetchTickets from '@/hooks/useApi/useFetchTickets';
import { usePermissions } from '@/providers/Permissions.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useFetchAccountUsers from '@/hooks/useApi/useFetchAccountUsers';
import { Button, TableFiltersContainer, Table, TransactionStatus, Title } from 'UI';
import TicketListSearchForm, { TicketSearchParams } from './TicketListSearchForm';
import CreateTicketDrawer from '@/components/Support/CreateTicketDrawer/CreateTicketDrawer';
import NoSubmittedTickets from './NoSubmittedTickets';
import EmptyOrNoResults from './EmptyOrNoResults';
import { useDataFetchIndicator } from '@/hooks';
import { useHistory, useLocation } from 'react-router-dom';

export type Tab = 'active' | 'closed';

type QueryParams = {
  user: string;
  tab: Tab;
};

const TicketsPage = () => {
  const { permissions } = usePermissions();
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'TicketsList' });
  const alertDialog = useAlertDialog();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const { fetchTickets } = useFetchTickets();
  const [count, setCount] = useState(0);
  // TODO: swap over to useAccountUsers (fetchUsers)
  const [fetchUsersResponse, fetchUsers] = useFetchAccountUsers({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { query, setQuery } = useQuery();
  const location = useLocation<{ openCreateTicket?: boolean } | undefined>();

  useEffect(() => {
    if (location.state?.openCreateTicket && permissions.CreateSupportTickets) {
      setIsDrawerOpen(true);
    }
  }, []);
  const tab: Tab = query.tab || 'active';
  const [[fetching], setFetching] = useDataFetchIndicator();
  const filtersApplied = !!query.user;

  function handleTabChange(value: string) {
    if (value === 'active' || value === 'closed') {
      setQuery({
        ...query,
        tab: value,
      });
    }
  }

  async function fetchTicketCount() {
    try {
      setFetching(true);

      const res = await fetchTickets({
        Email: '',
      });

      if (!res.Success) throw new Error(res.ErrorMessage);

      setCount(res.Tickets.length);
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        alertDialog.error(t('ErrorMessages.error'), err.message);
      }
    } finally {
      setFetching(false);
    }
  }

  async function handleTicketSubmitted() {
    setIsDrawerOpen(false);
    setFetching(true);

    try {
      await Promise.all([search(), fetchTicketCount()]);
    } finally {
      setFetching(false);
    }
  }

  async function search() {
    try {
      setFetching(true);

      const res = await fetchTickets({
        Email: query.user === 'all' ? '' : query.user,
        Timezone: userSettings.timezone,
      });

      setTickets(res.Tickets);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchTickets');
      alertDialog.error(t('ErrorMessages.error'), msg);
      console.error(err);
    } finally {
      setFetching(false);
    }
  }

  const hasTickets = useMemo(() => count > 0, [count]);

  useEffect(() => {
    search();
  }, [query]);

  useEffect(() => {
    setFetching(true);

    Promise.all([
      fetchTicketCount(),
      ...(permissions.ViewUsers ? [fetchUsers({ Timezone: userSettings.timezone })] : []),
    ])
      .catch((err) => {
        console.error(err);

        if (err instanceof Error) {
          alertDialog.error(t('ErrorMessages.error'), err.message);
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }, []);

  const users = useMemo(() => {
    if (!fetchUsersResponse.Success) return [];

    return Object.values(fetchUsersResponse.Users).map((user) => user.Username);
  }, [fetchUsersResponse]);

  async function handleSearchClick(params: TicketSearchParams) {
    setQuery({
      ...query,
      user: params.user,
    });
  }

  const filteredRows = fetching
    ? []
    : tab === 'active'
      ? tickets.filter((ticket) => ticket.id !== null && ticket.status !== 'Solved' && ticket.status !== 'Closed')
      : tickets.filter((ticket) => ticket.id !== null && (ticket.status === 'Solved' || ticket.status === 'Closed'));

  return (
    <Box flx="row" jc="center">
      <Box flx="col" gapSize="36px" width="100%">
        <Box jc="sb" ai="center" width="100%">
          <Title>{t('title')}</Title>

          {permissions.CreateSupportTickets && hasTickets && (
            <Button id="tickets-addTicket" colorScheme="primary" size="large" onClick={() => setIsDrawerOpen(true)}>
              {t('submitTicketButton')}
            </Button>
          )}

          <CreateTicketDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onSuccess={handleTicketSubmitted}
          />
        </Box>

        {hasTickets && (
          <CardTabs.Root value={tab} onValueChange={handleTabChange}>
            <CardTabs.List aria-label="Ticket List">
              <CardTabs.Trigger value="active">
                <span>{t('Tabs.activeTickets')}</span>
              </CardTabs.Trigger>

              <CardTabs.Trigger value="closed">
                <span>{t('Tabs.closedTickets')}</span>
              </CardTabs.Trigger>
            </CardTabs.List>

            {(['active', 'closed'] as const satisfies Tab[]).map((tab) => (
              <CardTabs.Content key={tab} value={tab}>
                {permissions.ViewUsers && (
                  <Box flx="col" gap="sm">
                    <TableFiltersContainer filtersApplied={filtersApplied}>
                      <TableFiltersContainer.Trigger />

                      <TableFiltersContainer.Content>
                        <TicketListSearchForm user={query.user} onSearch={handleSearchClick} users={users} />
                      </TableFiltersContainer.Content>
                    </TableFiltersContainer>
                  </Box>
                )}

                <Box>
                  <Table
                    id="tickets-list"
                    zebra
                    columns={[
                      {
                        id: 'ticketId',
                        header: t('Columns.ticketId'),
                        align: 'left',
                        template: ({ data }) => {
                          return <Table.Link to={{ pathname: `tickets/${data.id}` }}>{data.id}</Table.Link>;
                        },
                      },
                      {
                        id: 'status',
                        header: t('Columns.status'),
                        align: 'left',
                        template: ({ data }) => {
                          return (
                            <TransactionStatus
                              status={GetColorStatus(data.status || '')}
                              label={capitalizeFirstLetter(data.status || '')}
                            />
                          );
                        },
                      },
                      { id: 'subject', header: t('Columns.subject'), align: 'left', field: 'subject' },
                      { id: 'dateAdded', header: t('Columns.dateAdded'), align: 'left', field: 'dateAdded' },
                      { id: 'dateUpdated', header: t('Columns.dateUpdated'), align: 'left', field: 'lastUpdated' },
                    ]}
                    rows={filteredRows.map((item): { id: string; data: Ticket } => ({
                      id: String(item.id || ''),
                      data: item,
                    }))}
                  />
                </Box>

                {fetching && (
                  <div
                    style={{
                      height: '60vh',
                    }}
                  ></div>
                )}

                {!fetching && filteredRows.length === 0 && <EmptyOrNoResults activeTab={tab} />}
              </CardTabs.Content>
            ))}
          </CardTabs.Root>
        )}

        {!fetching && !hasTickets && <NoSubmittedTickets onCreateTicketClick={() => setIsDrawerOpen(true)} />}
      </Box>
    </Box>
  );
};

export default TicketsPage;

function useQuery() {
  const location = useLocation();
  const { search } = location;
  const history = useHistory();
  const [c, setC] = useState(0);

  const query = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(search)) as QueryParams;
  }, [c]);

  const filtersApplied = useMemo(() => Object.keys(query).length > 0, [query]);

  function setQuery(data: QueryParams) {
    const newParams = new URLSearchParams(
      Object.entries(data).reduce((acc, [key, val]) => {
        if (!val) return acc;

        return {
          ...acc,
          [key]: val,
        };
      }, {}),
    );

    history.replace({ pathname: location.pathname, search: newParams.toString() });

    // if user clicks "search" without changing anything, the url won't update and thus won't retrigger a search. this forces a refresh.
    setC((prev) => prev + 1);
  }

  return {
    query,
    setQuery,
    filtersApplied,
  };
}
